import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface EvaluationRow {
  readonly evaluationId: string;
  readonly modelCode: string | null;
  readonly modelName: string | null;
  readonly evaluationType: string;
  readonly accuracy: string | null;
  readonly sampleSize: string;
  readonly passed: boolean;
}

const EVALUATION_TYPES = ['', 'VALIDATION', 'BACKTEST', 'PERIODIC', 'DRIFT_CHECK'];

/**
 * M25 — Model Evaluation Dashboard (§15/§28).
 * Lists model evaluation runs with their accuracy, sample size and pass/fail
 * against the configured minimum. Drift is detected when the latest accuracy
 * falls below the threshold, prompting retraining.
 */
export function M25ModelEvaluationDashboardPage(): ReactNode {
  const { t: tr } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<EvaluationRow[]>([]);
  const [total, setTotal] = useState(0);
  const [evaluationType, setEvaluationType] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const query = evaluationType ? `?evaluationType=${encodeURIComponent(evaluationType)}` : '';
    apiRequest<{ data: EvaluationRow[]; total: number }>(`/v1/ai/evaluations${query}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, evaluationType]);

  return (
    <main aria-label="evaluation-dashboard">
      <h1>{tr('m25.evaluation.title')}</h1>
      <label htmlFor="evaluation-type-filter">{tr('m25.evaluation.filterLabel')}</label>
      <select id="evaluation-type-filter" value={evaluationType} onChange={(e) => setEvaluationType(e.target.value)}>
        {EVALUATION_TYPES.map((t) => <option key={t || 'all'} value={t}>{t || tr('m25.evaluation.allTypes')}</option>)}
      </select>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{tr('m25.evaluation.loading')}</p> : (
        <>
          <p aria-label="evaluation-count">{tr('m25.evaluation.count', { count: total })}</p>
          <table aria-label="evaluation-table">
            <thead>
              <tr><th>{tr('m25.evaluation.colModel')}</th><th>{tr('m25.evaluation.colType')}</th><th>{tr('m25.evaluation.colAccuracy')}</th><th>{tr('m25.evaluation.colSampleSize')}</th><th>{tr('m25.evaluation.colResult')}</th></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.evaluationId}>
                  <td>{[r.modelCode, r.modelName].filter(Boolean).join(' \u2014 ')}</td>
                  <td>{r.evaluationType}</td>
                  <td>{r.accuracy ?? '—'}</td>
                  <td>{r.sampleSize}</td>
                  <td>{r.passed ? tr('m25.evaluation.passed') : tr('m25.evaluation.failed')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </main>
  );
}
